import { InfoIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  quoteFor,
  rejectedFields,
  useAcceptOffer,
  useDeclineOffer,
  type AcceptanceRefusal,
  type Mandate,
  type NegotiationDetail,
  type Offer,
} from '@entities/negotiation';
import { ApiError } from '@shared/api/problem';
import { money, moneyOf } from '@shared/lib/money';
import { percentValue } from '@shared/lib/percent';
import { Button, TextField } from '@shared/ui';

import styles from './OfferDecision.module.css';

interface OfferDecisionProps {
  detail: NegotiationDetail;
  offer: Offer;
  mandate: Mandate | null;
  /**
   * Существенное расхождение финальной сверки не подтверждено (§38.3).
   *
   * Признак приходит с сервера (`final_check.blocks_acceptance`), а не
   * вычисляется здесь: неактивная кнопка — подсказка, а правило живёт в
   * сервисном слое и отказывает и прямому запросу.
   */
  acceptBlocked?: boolean | undefined;
  /**
   * Согласие доступно (`accept_offer` в `available_actions`, BE-590). Если нет
   * — блок остаётся ради отказа от предложения, а кнопки «Согласиться» нет
   * вовсе: правило живёт на сервере, и кнопка, ведущая в `409`, хуже её
   * отсутствия.
   */
  acceptAvailable?: boolean | undefined;
  /** Почему согласие недоступно — код с сервера (`acceptance_refusal`). */
  acceptanceRefusal?: AcceptanceRefusal | null | undefined;
}

/**
 * Экран решения по финальному предложению (FE-174, MVP_PRD §22.7).
 *
 * Что здесь обязано быть и почему:
 *
 * * **предложение поле к полю против объявления и мандата**, разница в € и %.
 *   Без сравнения решение принимается вслепую;
 * * **цитата под каждым заполненным полем.** Поле без цитаты не заполняется
 *   вовсе (§23.9), а отброшенные при извлечении показываются отдельно: «мы
 *   этого не подтвердили» — тоже ответ;
 * * **оба действия в два шага и без оптимистичного UI** (CLAUDE.md §8):
 *   согласие уходит письмом автохаусу, отменить его нельзя;
 * * **дисклеймер о том, что это не заключение договора** — рядом с кнопкой, а
 *   не в подвале: согласие внутри CarViQ.DE не является акцептом оферты (§21.1).
 *
 * **Красная кнопка здесь одна — «Согласиться»** (DESIGN_SYSTEM §5.1): это
 * главное необратимое действие экрана. «Отказаться» — вторичная; во втором
 * шаге отказа «Подтвердить» закрывает переписку и поэтому деструктивная —
 * бордовым контуром, без заливки (§2.4).
 */
export function OfferDecision({
  detail,
  offer,
  mandate,
  acceptBlocked = false,
  acceptAvailable = true,
  acceptanceRefusal = null,
}: OfferDecisionProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const currency = detail.listing_price.currency;

  const accept = useAcceptOffer(detail.negotiation_id);
  const decline = useDeclineOffer(detail.negotiation_id);
  const [confirming, setConfirming] = useState<'accept' | 'decline' | null>(null);
  // Телефон необязателен и спрашивается один раз — здесь (§39.4). Без него
  // передача работает по почте, и поле об этом говорит прямо.
  const [phone, setPhone] = useState('');

  const busy = accept.isPending || decline.isPending;
  const failure = acceptErrorText(accept.error, t) ?? errorText(decline.error, t);
  const dropped = rejectedFields(offer);
  // Отказ показывается там, куда человек смотрит в момент нажатия: во втором
  // шаге — прямо над «Bestätigen» (BE-602, находка H-5: текст отказа стоял над
  // блоком подтверждения и был о «своём тексте», который здесь не пишут).
  const failureNote =
    failure === null ? null : (
      <p className={styles.error} role="alert">
        <WarningCircleIcon size={16} aria-hidden="true" className={styles.noteIcon} />
        <span>{failure}</span>
      </p>
    );

  const rows: { field: string; value: string | null }[] = [
    {
      field: 'price_cents',
      value: offer.price === null ? null : moneyOf(offer.price, locale),
    },
    {
      field: 'total_cost_cents',
      value: offer.total_cost === null ? null : moneyOf(offer.total_cost, locale),
    },
    { field: 'valid_until', value: offer.valid_until },
  ];

  return (
    <section className={styles.wrap} aria-label={t('negotiation.decision.title')}>
      <h2 className={styles.title}>{t('negotiation.decision.title')}</h2>

      <dl className={styles.compare}>
        <div className={styles.row}>
          <dt>{t('negotiation.decision.listingPrice')}</dt>
          <dd>{moneyOf(detail.listing_price, locale)}</dd>
        </div>
        {mandate === null ? null : (
          <div className={styles.row}>
            <dt>{t('negotiation.decision.targetPrice')}</dt>
            <dd>{moneyOf(mandate.target_price, locale)}</dd>
          </div>
        )}
        <div className={styles.row}>
          <dt>{t('negotiation.decision.offerPrice')}</dt>
          <dd className={styles.strong}>
            {offer.price === null ? t('common.noData') : moneyOf(offer.price, locale)}
          </dd>
        </div>
        {offer.delta_vs_listing_cents === null ? null : (
          <div className={styles.row}>
            <dt>{t('negotiation.decision.delta')}</dt>
            <dd className={styles.strong}>
              {money(offer.delta_vs_listing_cents, currency, locale, 'auto')}
              {offer.delta_vs_listing_percent === null
                ? ''
                : ` · ${percentValue(offer.delta_vs_listing_percent, locale)} %`}
            </dd>
          </div>
        )}
      </dl>

      {/* Цитата-основание под каждым полем: «со слов автохауса, CarViQ.DE не
          проверял» — это не оговорка, а способ существования этих данных. */}
      <ul className={styles.evidence}>
        {rows.map(({ field, value }) => {
          const quote = quoteFor(offer, field);
          if (value === null || quote === null) return null;
          return (
            <li key={field}>
              <span className={styles.field}>{t(`negotiation.offerField.${field}`)}</span>
              <blockquote className={styles.quote}>{quote}</blockquote>
            </li>
          );
        })}
      </ul>
      <p className={styles.disclaimerQuote}>{t('negotiation.decision.quoteDisclaimer')}</p>

      {dropped.length === 0 ? null : (
        <p className={styles.dropped}>
          {t('negotiation.decision.dropped', {
            fields: dropped.map((item) => t(`negotiation.offerField.${item.field}`)).join(', '),
          })}
        </p>
      )}

      <p className={styles.disclaimer}>
        <InfoIcon size={16} aria-hidden="true" className={styles.noteIcon} />
        <span>{t('negotiation.decision.notAContract')}</span>
      </p>

      {confirming === null ? failureNote : null}

      {/* Согласие недоступно (BE-590): кнопки нет, причина названа словами,
          отказаться от предложения можно по-прежнему. */}
      {acceptAvailable ? null : (
        <p className={styles.blocked} role="note">
          <InfoIcon size={16} aria-hidden="true" className={styles.noteIcon} />
          <span>{unavailableText(acceptanceRefusal, t)}</span>
        </p>
      )}

      {/* «Согласиться» неактивна, пока существенное расхождение не
          подтверждено (FE-250). Кнопка не молчит: причина названа рядом. */}
      {acceptAvailable && acceptBlocked ? (
        <p className={styles.blocked} role="note">
          <WarningCircleIcon size={16} aria-hidden="true" className={styles.noteIcon} />
          <span>{t('negotiation.decision.blockedByFinalCheck')}</span>
        </p>
      ) : null}

      {confirming === null ? (
        <div className={styles.actions}>
          {acceptAvailable ? (
            <Button
              disabled={busy || acceptBlocked}
              onClick={() => {
                setConfirming('accept');
              }}
            >
              {t('negotiation.decision.accept')}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setConfirming('decline');
            }}
          >
            {t('negotiation.decision.decline')}
          </Button>
        </div>
      ) : (
        <div className={styles.confirm} role="group" aria-live="polite">
          <p className={styles.confirmText}>
            {confirming === 'accept'
              ? t('negotiation.decision.confirmAccept')
              : t('negotiation.decision.confirmDecline')}
          </p>
          {/* Согласие передаёт автохаусу контакт покупателя и код передачи
              (§39.4). Экран говорит об этом до нажатия, а не после: иначе
              человек узнаёт о переданном адресе из письма автохауса. */}
          {confirming === 'accept' ? (
            <>
              <p className={styles.disclaimer}>
                <InfoIcon size={16} aria-hidden="true" className={styles.noteIcon} />
                <span>{t('negotiation.decision.contactHandover')}</span>
              </p>
              <TextField
                label={t('negotiation.decision.phoneLabel')}
                hint={t('negotiation.decision.phoneHint')}
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                maxLength={32}
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                }}
              />
            </>
          ) : null}
          {failureNote}
          <div className={styles.actions}>
            <Button
              variant={confirming === 'accept' ? 'primary' : 'danger'}
              disabled={busy}
              onClick={() => {
                if (confirming === 'accept')
                  accept.mutate({ offerId: offer.offer_id, contactPhone: phone });
                else decline.mutate({ offerId: offer.offer_id, closeThread: true });
              }}
            >
              {busy ? t('negotiation.decision.pending') : t('negotiation.decision.confirmButton')}
            </Button>
            {confirming === 'decline' ? (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  decline.mutate({ offerId: offer.offer_id, closeThread: false });
                }}
              >
                {t('negotiation.decision.declineKeepGoing')}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                accept.reset();
                decline.reset();
                setConfirming(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function unavailableText(refusal: AcceptanceRefusal | null, t: (key: string) => string): string {
  const fallback = t('negotiation.decision.acceptUnavailable.default');
  if (refusal === null) return fallback;
  const key = `negotiation.decision.acceptUnavailable.${refusal}`;
  const translated = t(key);
  return translated === key ? fallback : translated;
}

/**
 * Отказ согласия. Письмо автохаусу, не прошедшее проверку, — не «поправьте
 * текст»: текста здесь человек не пишет. Экран говорит, что письмо не ушло и
 * согласие **не принято** — сервер откатывает его целиком (BE-590, BE-602).
 */
function acceptErrorText(error: unknown, t: (key: string) => string): string | null {
  if (error instanceof ApiError && error.code === 'LETTER_GUARDRAIL_FAILED') {
    return t('negotiation.decision.acceptLetterBlocked');
  }
  return errorText(error, t);
}

function errorText(error: unknown, t: (key: string) => string): string | null {
  if (!(error instanceof ApiError)) return null;
  const key = `negotiation.error.code.${error.code}`;
  const translated = t(key);
  return translated === key ? t('negotiation.error.text') : translated;
}
